package ai.jessie.shared.settings

import kotlinx.serialization.Serializable

// UC-025. UUIDs, dates (ISO yyyy-MM-dd) and the VAT rate (plain decimal, at most two places) are
// JSON strings. `revision` is an opaque stale-save token: return it unchanged; 0 means that the
// organisation is not yet configured.
@Serializable
data class VatRateResponse(
    val vatRateId: String,
    val name: String,
    val rate: String,
    val effectiveFrom: String,
    val effectiveTo: String? = null,
    // True only for the owner and only for a scheduled version that has not started.
    val editable: Boolean = false,
)

@Serializable
data class TaxonomyEntryResponse(val id: String, val name: String, val active: Boolean)

// A retained prefix and its increase-only floor: the minimum permitted next number (BR-179).
@Serializable data class InvoiceSequenceResponse(val prefix: String, val nextNumber: Int)

// Owner view (`editable`) and accountant finance view (A1) share this shape. The accountant view
// omits `automaticDocumentExtraction`, `invoiceSequences` and archived taxonomy. Business fields
// are absent while `configured` is false; nothing is fabricated.
@Serializable
data class OrganisationSettingsResponse(
    val editable: Boolean,
    val configured: Boolean,
    val revision: Long,
    val name: String,
    val timeZone: String,
    // Organisation-local current date: the earliest start of a new VAT version (BR-178).
    val currentDate: String,
    val address: String? = null,
    val countryCode: String? = null,
    val iban: String? = null,
    val vatNumber: String? = null,
    val invoicePrefix: String? = null,
    val nextInvoiceNumber: Int? = null,
    val defaultCurrency: String? = null,
    val invoiceLanguage: String? = null,
    val paymentTermsDays: Int? = null,
    val automaticDocumentExtraction: Boolean? = null,
    // System-assigned and read-only; absent until the inbound-mail use case assigns one.
    val inboundEmail: String? = null,
    val vatRates: List<VatRateResponse> = emptyList(),
    val invoiceSequences: List<InvoiceSequenceResponse> = emptyList(),
    val documentCategories: List<TaxonomyEntryResponse> = emptyList(),
    val documentFolders: List<TaxonomyEntryResponse> = emptyList(),
)

// Without `vatRateId`: a new version. With it: change (or with `remove`, delete) a scheduled
// version. Listed versions sent unchanged and versions left out stay as they are. The server sets
// every `effectiveTo`.
@Serializable
data class VatRateProposal(
    val name: String,
    val rate: String,
    val effectiveFrom: String,
    val vatRateId: String? = null,
    val remove: Boolean = false,
)

// Without `id`: create. With it: rename, archive (`active` false) or reactivate. Entries left out
// stay as they are; nothing is deleted.
@Serializable
data class TaxonomyProposal(val name: String, val active: Boolean = true, val id: String? = null)

// The complete proposed configuration, for validation (steps 4–5) and for the confirmed save
// (steps 7–9) alike.
@Serializable
data class SaveOrganisationSettingsRequest(
    val expectedRevision: Long,
    val name: String,
    val timeZone: String,
    val address: String,
    val countryCode: String,
    val invoicePrefix: String,
    val nextInvoiceNumber: Int,
    val defaultCurrency: String,
    val invoiceLanguage: String,
    val paymentTermsDays: Int,
    val automaticDocumentExtraction: Boolean,
    val iban: String? = null,
    val vatNumber: String? = null,
    val vatRates: List<VatRateProposal> = emptyList(),
    val documentCategories: List<TaxonomyProposal> = emptyList(),
    val documentFolders: List<TaxonomyProposal> = emptyList(),
)

// One exact field-level change; `field` uses the identifiers of the settings history.
@Serializable
data class SettingsChangeResponse(
    val field: String,
    val previous: String? = null,
    val new: String? = null,
)

// Step 5: what a confirmed save of the validated proposal would change. Nothing was saved.
@Serializable data class SettingsPreviewResponse(val changes: List<SettingsChangeResponse>)

// `field` is a request field, for lists with the request index, such as `vatRates[0].rate`.
// `minimum` accompanies BELOW_MINIMUM (A4).
@Serializable
data class SettingsFieldProblem(val field: String, val reason: String, val minimum: Int? = null)

// Superset of the common error body (`code`, `fields`). `current` accompanies STALE_SETTINGS (A6).
@Serializable
data class SettingsProblemResponse(
    val code: String,
    val fields: List<String> = emptyList(),
    val problems: List<SettingsFieldProblem> = emptyList(),
    val current: OrganisationSettingsResponse? = null,
)
